import { useState, useEffect, useMemo } from "react";
import { useAuth } from "../contexts/AuthContext";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Switch } from "../components/ui/switch";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Separator } from "../components/ui/separator";
import { toast } from "sonner";
import { BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, Legend } from "recharts";
import { TrendingUp, Receipt, PiggyBank, Percent, ArrowDown, Calculator, Shield, Download, Wallet } from "lucide-react";
import axios from "axios";

const MONTH_NAMES = { "01": "Gennaio", "02": "Febbraio", "03": "Marzo", "04": "Aprile", "05": "Maggio", "06": "Giugno", "07": "Luglio", "08": "Agosto", "09": "Settembre", "10": "Ottobre", "11": "Novembre", "12": "Dicembre" };
const SHORT_MONTHS = { "01": "Gen", "02": "Feb", "03": "Mar", "04": "Apr", "05": "Mag", "06": "Giu", "07": "Lug", "08": "Ago", "09": "Set", "10": "Ott", "11": "Nov", "12": "Dic" };
const MONTH_OPTIONS = [
  { value: "01", label: "Gennaio" }, { value: "02", label: "Febbraio" }, { value: "03", label: "Marzo" },
  { value: "04", label: "Aprile" }, { value: "05", label: "Maggio" }, { value: "06", label: "Giugno" },
  { value: "07", label: "Luglio" }, { value: "08", label: "Agosto" }, { value: "09", label: "Settembre" },
  { value: "10", label: "Ottobre" }, { value: "11", label: "Novembre" }, { value: "12", label: "Dicembre" },
];

const EUR = (v) => `\u20AC${(v || 0).toLocaleString("it-IT", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

function calcIRPEF(reddito) {
  if (reddito <= 0) return 0;
  let tassa = 0;
  if (reddito <= 28000) tassa = reddito * 0.23;
  else if (reddito <= 50000) tassa = 28000 * 0.23 + (reddito - 28000) * 0.35;
  else tassa = 28000 * 0.23 + 22000 * 0.35 + (reddito - 50000) * 0.43;
  return tassa;
}

function ChartTooltip({ active, payload, label }) {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white dark:bg-stone-800 border border-stone-200 dark:border-stone-700 rounded-sm shadow-lg p-3 text-xs min-w-[180px]">
      <p className="font-medium text-stone-900 dark:text-white mb-2">{label}</p>
      {payload.map((p, i) => (
        <div key={i} className="flex items-center justify-between gap-4 mb-0.5">
          <span className="flex items-center gap-1.5">
            <span className="w-2.5 h-2.5 rounded-sm" style={{ backgroundColor: p.color }} />
            <span className="text-stone-600 dark:text-stone-300">{p.name}</span>
          </span>
          <span className="font-mono font-medium">{EUR(p.value)}</span>
        </div>
      ))}
    </div>
  );
}

export default function EarningsPage() {
  const { user, API } = useAuth();
  const [data, setData] = useState(null);
  const [properties, setProperties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filterProp, setFilterProp] = useState("all");
  const [filterYear, setFilterYear] = useState("all");
  const [filterMonth, setFilterMonth] = useState("all");
  const [showTax, setShowTax] = useState(false);
  const [taxMode, setTaxMode] = useState("cedolare_21");

  const isAdmin = user?.role === "admin";
  const ownerTax = data?.owner_tax_info;
  const isSostituto = ownerTax?.sostituto_imposta === true;

  const handleExport = async (format) => {
    try {
      const params = new URLSearchParams();
      params.set("format", format);
      if (filterProp !== "all") params.set("property_id", filterProp);
      if (filterYear !== "all") params.set("year", filterYear);
      const res = await axios.get(`${API}/earnings/export?${params.toString()}`, { responseType: "blob" });
      const url = window.URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `guadagni_${filterYear !== "all" ? filterYear : "tutti"}.${format}`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
      toast.success(`Export ${format.toUpperCase()} scaricato`);
    } catch { toast.error("Errore nell'export"); }
  };

  const fetchData = (propFilter, yearFilter) => {
    setLoading(true);
    const params = {};
    if (propFilter && propFilter !== "all") params.property_id = propFilter;
    if (yearFilter && yearFilter !== "all") params.year = yearFilter;
    axios.get(`${API}/earnings`, { params })
      .then(res => {
        setData(res.data);
        if (res.data.owner_tax_info?.regime_fiscale) {
          setTaxMode(res.data.owner_tax_info.regime_fiscale);
        }
      })
      .catch(() => toast.error("Errore nel caricamento"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    axios.get(`${API}/properties`).then(res => setProperties(res.data)).catch(() => {});
    fetchData(filterProp, filterYear);
  }, [API]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => { fetchData(filterProp, filterYear); }, [filterProp, filterYear]); // eslint-disable-line react-hooks/exhaustive-deps

  const years = useMemo(() => {
    const yrs = [...new Set((data?.monthly || []).map(m => m.month.split("-")[0]))].sort().reverse();
    return yrs.length > 0 ? yrs : [new Date().getFullYear().toString()];
  }, [data]);

  const filteredMonthly = useMemo(() => {
    if (filterMonth === "all") return data?.monthly || [];
    return (data?.monthly || []).filter(m => m.month.split("-")[1] === filterMonth);
  }, [data, filterMonth]);

  const filteredSummary = useMemo(() => {
    if (filterMonth === "all") return data?.summary || {};
    return {
      lordo_fiscale: filteredMonthly.reduce((a, m) => a + (m.lordo_fiscale || 0), 0),
      commissioni_ota: filteredMonthly.reduce((a, m) => a + (m.commissioni_ota || 0), 0),
      pulizie: filteredMonthly.reduce((a, m) => a + (m.pulizie || 0), 0),
      costi_variabili: filteredMonthly.reduce((a, m) => a + (m.costi_variabili || 0), 0),
      sconti: filteredMonthly.reduce((a, m) => a + (m.sconti || 0), 0),
      rimborsi: filteredMonthly.reduce((a, m) => a + (m.rimborsi || 0), 0),
      netto_operativo: filteredMonthly.reduce((a, m) => a + (m.netto_operativo || 0), 0),
      base_pm: filteredMonthly.reduce((a, m) => a + (m.base_pm || 0), 0),
      pm_fee: filteredMonthly.reduce((a, m) => a + (m.pm_fee || 0), 0),
      netto_proprietario: filteredMonthly.reduce((a, m) => a + (m.netto_proprietario || 0), 0),
      total_bookings: filteredMonthly.reduce((a, m) => a + (m.bookings_count || 0), 0),
      costi_appartamento: filteredMonthly.reduce((a, m) => a + (m.costi_appartamento || 0), 0),
    };
  }, [data, filterMonth, filteredMonthly]);

  const s = filteredSummary;

  const chartData = filteredMonthly.map(m => ({
    name: SHORT_MONTHS[m.month.split("-")[1]] + " " + m.month.split("-")[0].slice(2),
    "Comm. OTA": Math.round(m.commissioni_ota || 0),
    "Pulizie": Math.round(m.pulizie || 0),
    "Costi Var.": Math.round(m.costi_variabili || 0),
    "Comm. PM": Math.round(m.pm_fee || 0),
    "Costi App.": Math.round(m.costi_appartamento || 0),
    "Netto Prop.": Math.round(m.netto_proprietario || 0),
  }));

  // Tax: Cedolare Secca on LORDO FISCALE, IRPEF on netto_proprietario
  const taxCalc = useMemo(() => {
    const baseLordo = s.lordo_fiscale || 0;
    const mode = isSostituto && ownerTax?.regime_fiscale ? ownerTax.regime_fiscale : taxMode;
    if (mode === "cedolare_21") {
      const tax = baseLordo * 0.21;
      return { rate: "21%", taxAmount: tax, netAfterTax: (s.netto_proprietario || 0) - tax, type: "Cedolare Secca", base: baseLordo };
    } else if (mode === "cedolare_26") {
      const tax = baseLordo * 0.26;
      return { rate: "26%", taxAmount: tax, netAfterTax: (s.netto_proprietario || 0) - tax, type: "Cedolare Secca", base: baseLordo };
    } else {
      const baseIrpef = s.netto_proprietario || 0;
      const tax = calcIRPEF(baseIrpef);
      const effectiveRate = baseIrpef > 0 ? ((tax / baseIrpef) * 100).toFixed(1) + "%" : "0%";
      return { rate: effectiveRate, taxAmount: tax, netAfterTax: baseIrpef - tax, type: "IRPEF Scaglioni", base: baseIrpef };
    }
  }, [s.lordo_fiscale, s.netto_proprietario, taxMode, isSostituto, ownerTax]);

  const kpis = [
    { title: "Lordo Fiscale", value: EUR(s.lordo_fiscale), icon: TrendingUp, color: "text-stone-900", bg: "bg-stone-100" },
    { title: "Netto Operativo", value: EUR(s.netto_operativo), icon: PiggyBank, color: "text-amber-600", bg: "bg-amber-50" },
    { title: "Comm. PM", value: EUR(s.pm_fee), icon: Percent, color: "text-blue-600", bg: "bg-blue-50" },
    { title: "Netto Proprietario", value: EUR(s.netto_proprietario), icon: Wallet, color: "text-green-700", bg: "bg-green-50" },
  ];

  return (
    <div className="p-6 lg:p-8 max-w-7xl mx-auto" data-testid="earnings-page">
      <div className="flex items-center justify-between mb-8">
        <div>
          <h1 className="font-heading text-3xl sm:text-4xl text-stone-900">Guadagni</h1>
          <p className="text-stone-500 mt-1 text-sm">Lordo fiscale, netto operativo, base provvigioni PM</p>
        </div>
        <div className="flex gap-2">
          <Button variant="outline" size="sm" onClick={() => handleExport("xlsx")} data-testid="export-xlsx-button" className="text-xs">
            <Download className="h-3.5 w-3.5 mr-1.5" /> Excel
          </Button>
          <Button variant="outline" size="sm" onClick={() => handleExport("csv")} data-testid="export-csv-button" className="text-xs">
            <Download className="h-3.5 w-3.5 mr-1.5" /> CSV
          </Button>
        </div>
      </div>

      {/* Filters */}
      <div className="flex gap-3 mb-6 flex-wrap">
        <Select value={filterProp} onValueChange={setFilterProp}>
          <SelectTrigger data-testid="earnings-property-filter" className="w-52 bg-white"><SelectValue placeholder="Proprietà" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutte le proprietà</SelectItem>
            {properties.map(p => <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterYear} onValueChange={v => { setFilterYear(v); setFilterMonth("all"); }}>
          <SelectTrigger data-testid="earnings-year-filter" className="w-32 bg-white"><SelectValue placeholder="Anno" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti gli anni</SelectItem>
            {years.map(y => <SelectItem key={y} value={y}>{y}</SelectItem>)}
          </SelectContent>
        </Select>
        <Select value={filterMonth} onValueChange={setFilterMonth}>
          <SelectTrigger data-testid="earnings-month-filter" className="w-40 bg-white"><SelectValue placeholder="Mese" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Tutti i mesi</SelectItem>
            {MONTH_OPTIONS.map(m => <SelectItem key={m.value} value={m.value}>{m.label}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-8 w-8 border-2 border-stone-300 border-t-stone-900" /></div>
      ) : (
        <>
          {/* KPI Cards */}
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
            {kpis.map((kpi, i) => (
              <Card key={i} className="border border-stone-200 shadow-sm">
                <CardContent className="p-5">
                  <div className={`h-9 w-9 rounded-sm ${kpi.bg} flex items-center justify-center mb-3`}>
                    <kpi.icon className={`h-[18px] w-[18px] ${kpi.color}`} strokeWidth={1.5} />
                  </div>
                  <p className="font-mono text-xl font-semibold text-stone-900" data-testid={`earnings-kpi-${i}`}>{kpi.value}</p>
                  <p className="text-xs text-stone-500 mt-1">{kpi.title}</p>
                </CardContent>
              </Card>
            ))}
          </div>

          {/* OTA Breakdown */}
          {data?.ota_breakdown && (
            <div className="grid grid-cols-3 gap-3 mb-6" data-testid="ota-breakdown">
              <div className="flex items-center gap-3 p-3 bg-blue-50 rounded-sm border border-blue-100">
                <div className="h-8 w-8 rounded-sm bg-blue-100 flex items-center justify-center"><span className="text-xs font-bold text-blue-700">B</span></div>
                <div><p className="text-xs text-blue-600">Booking.com</p><p className="font-mono text-lg font-semibold text-blue-800">{data.ota_breakdown.booking || 0}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-rose-50 rounded-sm border border-rose-100">
                <div className="h-8 w-8 rounded-sm bg-rose-100 flex items-center justify-center"><span className="text-xs font-bold text-rose-700">A</span></div>
                <div><p className="text-xs text-rose-600">Airbnb</p><p className="font-mono text-lg font-semibold text-rose-800">{data.ota_breakdown.airbnb || 0}</p></div>
              </div>
              <div className="flex items-center gap-3 p-3 bg-emerald-50 rounded-sm border border-emerald-100">
                <div className="h-8 w-8 rounded-sm bg-emerald-100 flex items-center justify-center"><span className="text-xs font-bold text-emerald-700">D</span></div>
                <div><p className="text-xs text-emerald-600">Dirette</p><p className="font-mono text-lg font-semibold text-emerald-800">{data.ota_breakdown.diretto || 0}</p></div>
              </div>
            </div>
          )}

          {/* Waterfall Summary */}
          <Card className="border border-stone-200 shadow-sm mb-6">
            <CardHeader className="pb-3"><CardTitle className="text-base font-medium text-stone-900">Riepilogo Finanziario</CardTitle></CardHeader>
            <CardContent data-testid="earnings-waterfall">
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <div className="flex items-center gap-2 px-3 py-2 bg-stone-100 rounded-sm border border-stone-200">
                  <span className="text-stone-500 text-xs">Lordo Fiscale</span>
                  <span className="font-mono font-semibold text-stone-900">{EUR(s.lordo_fiscale)}</span>
                </div>
                <ArrowDown className="h-3.5 w-3.5 text-stone-400 rotate-[-90deg] hidden sm:block" />
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-sm border border-red-100">
                  <span className="text-red-500 text-xs">Comm. OTA</span>
                  <span className="font-mono font-medium text-red-700">-{EUR(s.commissioni_ota)}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-sm border border-red-100">
                  <span className="text-red-500 text-xs">Pulizie</span>
                  <span className="font-mono font-medium text-red-700">-{EUR(s.pulizie)}</span>
                </div>
                <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-sm border border-red-100">
                  <span className="text-red-500 text-xs">Transazioni</span>
                  <span className="font-mono font-medium text-red-700">-{EUR(s.costi_variabili)}</span>
                </div>
                {(s.sconti || 0) > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-orange-50 rounded-sm border border-orange-100">
                    <span className="text-orange-500 text-xs">Sconti</span>
                    <span className="font-mono font-medium text-orange-700">-{EUR(s.sconti)}</span>
                  </div>
                )}
                <ArrowDown className="h-3.5 w-3.5 text-stone-400 rotate-[-90deg] hidden sm:block" />
                <div className="flex items-center gap-2 px-3 py-2 bg-amber-50 rounded-sm border border-amber-200">
                  <span className="text-amber-700 font-medium text-xs">Netto Op.</span>
                  <span className="font-mono font-bold text-amber-800">{EUR(s.netto_operativo)}</span>
                </div>
                {(s.rimborsi || 0) > 0 && (
                  <>
                    <ArrowDown className="h-3.5 w-3.5 text-stone-400 rotate-[-90deg] hidden sm:block" />
                    <div className="flex items-center gap-2 px-3 py-2 bg-purple-50 rounded-sm border border-purple-100">
                      <span className="text-purple-500 text-xs">Rimborsi</span>
                      <span className="font-mono font-medium text-purple-700">-{EUR(s.rimborsi)}</span>
                    </div>
                  </>
                )}
                <ArrowDown className="h-3.5 w-3.5 text-stone-400 rotate-[-90deg] hidden sm:block" />
                <div className="flex items-center gap-2 px-3 py-2 bg-blue-50 rounded-sm border border-blue-100">
                  <span className="text-blue-500 text-xs">Comm. PM</span>
                  <span className="font-mono font-medium text-blue-700">-{EUR(s.pm_fee)}</span>
                </div>
                {(s.costi_appartamento || 0) > 0 && (
                  <div className="flex items-center gap-2 px-3 py-2 bg-red-50 rounded-sm border border-red-100">
                    <span className="text-red-500 text-xs">Costi App.</span>
                    <span className="font-mono font-medium text-red-700">-{EUR(s.costi_appartamento)}</span>
                  </div>
                )}
                <ArrowDown className="h-3.5 w-3.5 text-stone-400 rotate-[-90deg] hidden sm:block" />
                <div className="flex items-center gap-2 px-3 py-3 bg-green-50 rounded-sm border border-green-200">
                  <span className="text-green-700 font-medium text-xs">Netto Prop.</span>
                  <span className="font-mono font-bold text-green-800">{EUR(s.netto_proprietario)}</span>
                </div>
              </div>
            </CardContent>
          </Card>

          {/* Chart */}
          <Card className="border border-stone-200 shadow-sm mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-base font-medium text-stone-900">Breakdown Mensile</CardTitle></CardHeader>
            <CardContent data-testid="earnings-bar-chart">
              {chartData.length > 0 ? (
                <ResponsiveContainer width="100%" height={320}>
                  <BarChart data={chartData} stackOffset="none">
                    <XAxis dataKey="name" tick={{ fill: '#57534E', fontSize: 11, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} />
                    <YAxis tick={{ fill: '#A8A29E', fontSize: 10, fontFamily: 'JetBrains Mono' }} axisLine={false} tickLine={false} tickFormatter={v => `\u20AC${v}`} width={60} />
                    <Tooltip content={<ChartTooltip />} />
                    <Legend wrapperStyle={{ fontSize: '11px', fontFamily: 'Manrope' }} />
                    <Bar dataKey="Netto Prop." stackId="a" fill="#15803D" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Comm. PM" stackId="a" fill="#3B82F6" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Costi App." stackId="a" fill="#9333EA" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Costi Var." stackId="a" fill="#F97316" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Pulizie" stackId="a" fill="#EAB308" radius={[0, 0, 0, 0]} />
                    <Bar dataKey="Comm. OTA" stackId="a" fill="#DC2626" radius={[3, 3, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              ) : <div className="h-[320px] flex items-center justify-center text-stone-400 text-sm">Nessun dato per il periodo selezionato</div>}
            </CardContent>
          </Card>

          {/* Monthly Detail Table */}
          <Card className="border border-stone-200 shadow-sm mb-6">
            <CardHeader className="pb-2"><CardTitle className="text-base font-medium text-stone-900">Dettaglio Mensile</CardTitle></CardHeader>
            <CardContent className="p-0 overflow-x-auto" data-testid="earnings-detail-table">
              <Table>
                <TableHeader>
                  <TableRow className="bg-stone-50">
                    <TableHead className="text-xs font-medium text-stone-500">Mese</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Lordo Fiscale</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Comm. OTA</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Pulizie</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Trans.</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right bg-amber-50">Netto Op.</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Base PM</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Comm. PM</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Costi App.</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right bg-green-50">Netto Prop.</TableHead>
                    <TableHead className="text-xs font-medium text-stone-500 text-right">Pren.</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredMonthly.length === 0 ? (
                    <TableRow><TableCell colSpan={11} className="text-center text-stone-400 py-8">Nessun dato per il periodo selezionato</TableCell></TableRow>
                  ) : (<>
                    {filteredMonthly.map((m, i) => (
                      <TableRow key={i} className="hover:bg-stone-50">
                        <TableCell className="text-sm font-medium text-stone-900">{MONTH_NAMES[m.month.split("-")[1]]} {m.month.split("-")[0]}</TableCell>
                        <TableCell className="text-sm font-mono text-stone-600 text-right">{EUR(m.lordo_fiscale)}</TableCell>
                        <TableCell className="text-sm font-mono text-red-500 text-right">-{EUR(m.commissioni_ota)}</TableCell>
                        <TableCell className="text-sm font-mono text-stone-500 text-right">-{EUR(m.pulizie)}</TableCell>
                        <TableCell className="text-sm font-mono text-stone-500 text-right">-{EUR(m.costi_variabili)}</TableCell>
                        <TableCell className="text-sm font-mono font-semibold text-amber-700 text-right bg-amber-50/50">{EUR(m.netto_operativo)}</TableCell>
                        <TableCell className="text-sm font-mono text-stone-500 text-right">{EUR(m.base_pm)}</TableCell>
                        <TableCell className="text-sm font-mono text-blue-600 text-right">-{EUR(m.pm_fee)}</TableCell>
                        <TableCell className="text-sm font-mono text-purple-600 text-right">{m.costi_appartamento > 0 ? `-${EUR(m.costi_appartamento)}` : "-"}</TableCell>
                        <TableCell className="text-sm font-mono font-bold text-green-700 text-right bg-green-50/50">{EUR(m.netto_proprietario)}</TableCell>
                        <TableCell className="text-sm font-mono text-stone-600 text-right">{m.bookings_count}</TableCell>
                      </TableRow>
                    ))}
                    <TableRow className="bg-stone-50 font-semibold border-t-2 border-stone-300">
                      <TableCell className="text-sm text-stone-900">TOTALE</TableCell>
                      <TableCell className="text-sm font-mono text-stone-900 text-right">{EUR(s.lordo_fiscale)}</TableCell>
                      <TableCell className="text-sm font-mono text-red-600 text-right">-{EUR(s.commissioni_ota)}</TableCell>
                      <TableCell className="text-sm font-mono text-stone-700 text-right">-{EUR(s.pulizie)}</TableCell>
                      <TableCell className="text-sm font-mono text-stone-700 text-right">-{EUR(s.costi_variabili)}</TableCell>
                      <TableCell className="text-sm font-mono font-bold text-amber-800 text-right bg-amber-50">{EUR(s.netto_operativo)}</TableCell>
                      <TableCell className="text-sm font-mono text-stone-700 text-right">{EUR(s.base_pm)}</TableCell>
                      <TableCell className="text-sm font-mono text-blue-700 text-right">-{EUR(s.pm_fee)}</TableCell>
                      <TableCell className="text-sm font-mono text-purple-700 text-right">{s.costi_appartamento > 0 ? `-${EUR(s.costi_appartamento)}` : "-"}</TableCell>
                      <TableCell className="text-sm font-mono font-bold text-green-800 text-right bg-green-50">{EUR(s.netto_proprietario)}</TableCell>
                      <TableCell className="text-sm font-mono text-stone-900 text-right">{s.total_bookings}</TableCell>
                    </TableRow>
                  </>)}
                </TableBody>
              </Table>
            </CardContent>
          </Card>

          {/* Tax Section */}
          {!isAdmin && isSostituto ? (
            <Card className="border border-green-200 shadow-sm bg-green-50/30" data-testid="tax-automatic-card">
              <CardHeader className="pb-3">
                <div className="flex items-center gap-3">
                  <Shield className="h-5 w-5 text-green-600" strokeWidth={1.5} />
                  <CardTitle className="text-base font-medium text-stone-900">Detrazione Tasse</CardTitle>
                  <Badge className="text-[10px] bg-green-100 text-green-700 border-green-300">Sostituto d'imposta</Badge>
                </div>
                <p className="text-xs text-stone-500 mt-1">Le tasse sono calcolate e trattenute automaticamente dal gestore</p>
              </CardHeader>
              <CardContent data-testid="tax-automatic-content">
                <div className="grid sm:grid-cols-3 gap-4 mb-4">
                  <div className="p-4 bg-white rounded-sm border border-green-200">
                    <p className="text-xs text-green-600 mb-1">Lordo Fiscale (base imponibile)</p>
                    <p className="font-mono text-xl font-bold text-green-800">{EUR(taxCalc.base)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-sm border border-red-200">
                    <p className="text-xs text-red-500 mb-1">Tasse trattenute ({taxCalc.type} {taxCalc.rate})</p>
                    <p className="font-mono text-xl font-bold text-red-700">-{EUR(taxCalc.taxAmount)}</p>
                  </div>
                  <div className="p-4 bg-white rounded-sm border border-emerald-300">
                    <p className="text-xs text-emerald-600 mb-1">Netto dopo tasse</p>
                    <p className="font-mono text-xl font-bold text-emerald-800">{EUR(taxCalc.netAfterTax)}</p>
                  </div>
                </div>
              </CardContent>
            </Card>
          ) : (
            <Card className="border border-stone-200 shadow-sm" data-testid="tax-simulation-card">
              <CardHeader className="pb-3">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-3">
                    <Calculator className="h-5 w-5 text-stone-400" strokeWidth={1.5} />
                    <CardTitle className="text-base font-medium text-stone-900">Simulazione Tasse</CardTitle>
                    <Badge variant="outline" className="text-[10px] bg-stone-50 text-stone-500 border-stone-200">Opzionale</Badge>
                  </div>
                  <div className="flex items-center gap-2">
                    <Label htmlFor="tax-toggle" className="text-xs text-stone-500">Mostra</Label>
                    <Switch id="tax-toggle" data-testid="tax-simulation-toggle" checked={showTax} onCheckedChange={setShowTax} />
                  </div>
                </div>
              </CardHeader>
              {showTax && (
                <CardContent data-testid="tax-simulation-content">
                  <Separator className="mb-5" />
                  <div className="flex gap-3 mb-6 flex-wrap">
                    <Select value={taxMode} onValueChange={setTaxMode}>
                      <SelectTrigger data-testid="tax-mode-select" className="w-64 bg-white"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="cedolare_21">Cedolare Secca 21%</SelectItem>
                        <SelectItem value="cedolare_26">Cedolare Secca 26%</SelectItem>
                        <SelectItem value="irpef">Scaglioni IRPEF 2025</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="grid sm:grid-cols-3 gap-4 mb-6">
                    <div className="p-4 bg-green-50 rounded-sm border border-green-200">
                      <p className="text-xs text-green-600 mb-1">Base imponibile ({taxCalc.type === "IRPEF Scaglioni" ? "Netto Prop." : "Lordo Fiscale"})</p>
                      <p className="font-mono text-xl font-bold text-green-800">{EUR(taxCalc.base)}</p>
                    </div>
                    <div className="p-4 bg-red-50 rounded-sm border border-red-200">
                      <p className="text-xs text-red-500 mb-1">Tasse stimate ({taxCalc.type} {taxCalc.rate})</p>
                      <p className="font-mono text-xl font-bold text-red-700">-{EUR(taxCalc.taxAmount)}</p>
                    </div>
                    <div className="p-4 bg-emerald-50 rounded-sm border border-emerald-300">
                      <p className="text-xs text-emerald-600 mb-1">Netto Pulito (dopo tasse)</p>
                      <p className="font-mono text-xl font-bold text-emerald-800">{EUR(taxCalc.netAfterTax)}</p>
                    </div>
                  </div>
                  <p className="text-[10px] text-stone-400 mt-4">
                    Questa è una simulazione indicativa. Per il calcolo esatto delle tasse consultare il proprio commercialista.
                  </p>
                </CardContent>
              )}
            </Card>
          )}
        </>
      )}
    </div>
  );
}
